import { UserModel } from '$tablet-app/src/app/auth/UserModel';
import NewButton from '$tablet-app/src/app/components/Button/NewButton';
import PopUpMenu from '$tablet-app/src/app/components/PopUpMenu';
import Row from '$tablet-app/src/app/components/Row';
import Spacer from '$tablet-app/src/app/components/Spacer';
import Text from '$tablet-app/src/app/components/Text';
import {
  filterUsersSharedForStickyNote,
  getStickyNoteFontStyle,
  getStickyNoteUserAcknowledgement,
} from '$tablet-app/src/app/sticky-notes/functions';
import { StickyNote } from '$tablet-app/src/app/sticky-notes/types';
import { palette } from '$tablet-app/src/app/theme';
import { User } from '@akin-home/amplify';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import React, { FC } from 'react';
import { StyleProp, View, ViewStyle } from 'react-native';
import styled from 'styled-components/native';
import StickyNoteSharedWithDisplay from '../StickyNoteSharedWithDisplay';
import StickyOwner from '../StickyOwner';

interface Props {
  sticky_note: StickyNote;
  current_user: UserModel;
  users?: User[];
  style?: StyleProp<ViewStyle>;
  disabled?: boolean;

  bottom_left?: React.ReactNode;
  bottom_right?: React.ReactNode;

  onPress?: (sticky_note: StickyNote) => void;
  onPressDelete?: (sticky_note: StickyNote) => void;
  onPressEdit?: (sticky_note: StickyNote) => void;
  onPressShare?: (sticky_note: StickyNote) => void;
  onPressAcknowledge?: (sticky_note: StickyNote) => void;
}

const StickyNoteSheet: FC<Props> = (props) => {
  const {
    sticky_note,
    current_user,
    users,
    style,
    disabled = false,

    bottom_left,

    onPressAcknowledge,
    onPressDelete,
    onPressEdit,
  } = props;

  const acknowledged = !!getStickyNoteUserAcknowledgement(
    sticky_note,
    current_user.id
  );
  const isOwner = current_user.id === sticky_note.owner?.id;
  const sharedWithUsers = filterUsersSharedForStickyNote(sticky_note, users);

  const onPress = () => props.onPress?.(sticky_note);
  const handleOnPressAcknowledge = () => onPressAcknowledge?.(sticky_note);

  const textStyle = getStickyNoteFontStyle(sticky_note);

  return (
    <Container color={sticky_note.color || palette.pastel_green} style={style}>
      <InnerContainer>
        <Row justifyContent="space-between">
          {isOwner ? (
            <StickyNoteSharedWithDisplay users={sharedWithUsers} />
          ) : (
            <StickyOwner owner={sticky_note.owner} />
          )}

          {isOwner ? (
            <PopUpMenu
              anchor={({ showPopUp }) => (
                <NewButton
                  variant="text"
                  icon="ellipsis-vertical"
                  onPress={showPopUp}
                />
              )}
            >
              {({ hidePopUp }) => (
                <>
                  {onPressEdit ? (
                    <MenuItem
                      onPress={() => {
                        onPressEdit?.(sticky_note);
                        hidePopUp();
                      }}
                    >
                      <FontAwesomeIcon icon="edit" color="#828999" />
                      <Spacer width="18px" />
                      <Text>Edit</Text>
                    </MenuItem>
                  ) : null}

                  <MenuItem
                    onPress={() => {
                      onPressDelete?.(sticky_note);
                      hidePopUp();
                    }}
                  >
                    <FontAwesomeIcon icon="trash" color="#828999" />
                    <Spacer width="18px" />
                    <Text>Delete</Text>
                  </MenuItem>
                </>
              )}
            </PopUpMenu>
          ) : null}
        </Row>
        <Content onPress={onPress} disabled={disabled}>
          <ContentText preset="stickyNote" style={textStyle}>
            {sticky_note.content}
          </ContentText>
        </Content>
        <BottomRow>
          <View>{bottom_left}</View>

          {onPressAcknowledge ? (
            <ThumbButton
              iconSize={20}
              variant="text"
              icon={acknowledged ? ['fas', 'thumbs-up'] : ['far', 'thumbs-up']}
              onPress={handleOnPressAcknowledge}
            />
          ) : null}
        </BottomRow>
      </InnerContainer>
    </Container>
  );
};

const Container = styled(View)<{ color: string }>`
  border-radius: 22px;
  height: 98%;
  background-color: ${(props) => props.color};
`;

const InnerContainer = styled.View`
  height: 100%;
  width: 100%;
  padding: 16px;
`;

const ContentText = styled(Text)`
  text-align: center;
`;

const Content = styled.TouchableOpacity`
  flex: 1;
  align-items: center;
  justify-content: center;
`;

const BottomRow = styled(Row)`
  justify-content: space-between;
  align-items: center;
`;

const MenuItem = styled.TouchableOpacity`
  flex-direction: row;

  align-items: center;

  height: 55px;
  width: 320px;

  padding: 0 20px;
`;

const ThumbButton = styled(NewButton)`
  opacity: 0.5;
`;

export default StickyNoteSheet;
